<template>
  <div class="ts-app">
    <!-- Live timestamps -->
    <div class="ts-live-bar">
      <div class="ts-live-item">
        <span class="ts-live-label">{{ $t('tool.TimestampTool.unixSeconds') }}</span>
        <code class="ts-live-val">{{ nowSec }}</code>
        <button class="ts-btn-icon" @click="copy(nowSec+'')" title="复制">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        </button>
      </div>
      <div class="ts-live-divider"></div>
      <div class="ts-live-item">
        <span class="ts-live-label">{{ $t('tool.TimestampTool.unixMillis') }}</span>
        <code class="ts-live-val">{{ nowMs }}</code>
        <button class="ts-btn-icon" @click="copy(nowMs+'')" title="复制">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        </button>
      </div>
      <div class="ts-live-divider"></div>
      <div class="ts-live-item ts-live-date">
        <span class="ts-live-label">{{ $t('tool.TimestampTool.localTime') }}</span>
        <span class="ts-live-date-val">{{ nowDate }}</span>
      </div>
    </div>

    <!-- Bidirectional conversion -->
    <div class="ts-body">
      <!-- Timestamp → Date -->
      <div class="ts-panel">
        <div class="ts-panel-head">
          <span class="ts-panel-badge in">{{ $t('tool.TimestampTool.input') }}</span>
          <span class="ts-panel-title">{{ $t('tool.TimestampTool.tsToDate') }}</span>
          <span class="ts-panel-sub">{{ $t('tool.TimestampTool.tsInputHint') }}</span>
        </div>
        <div class="ts-panel-input-row">
          <input class="ts-input" v-model="tsInput" @input="tsToDate" :placeholder="$t('tool.TimestampTool.phTs')"
            @keyup.enter="tsToDate"/>
          <button class="ts-btn-sm ts-btn-now" @click="tsInput=String(nowSec);tsToDate()">{{ $t('tool.TimestampTool.nowBtn') }}</button>
        </div>
        <div class="ts-panel-err" v-if="tsInput && tsErr">{{ tsErr }}</div>
        <div class="ts-panel-result" v-if="tsResult">
          <div class="ts-result-row" v-for="r in tsResult" :key="r.label">
            <span class="ts-result-label">{{ r.label }}</span>
            <code class="ts-result-val">{{ r.value }}</code>
            <button v-if="r.copy" class="ts-btn-icon ts-btn-sml" @click="copy(r.value)" title="复制">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            </button>
          </div>
        </div>
        <div class="ts-panel-hint" v-if="!tsInput">
          <span>{{ $t('tool.TimestampTool.tryTs') }}</span>
          <button class="ts-hint-chip" @click="tsInput='1717084800';tsToDate()">1717084800</button>
        </div>
      </div>

      <!-- Date → Timestamp -->
      <div class="ts-panel">
        <div class="ts-panel-head">
          <span class="ts-panel-badge out">{{ $t('tool.TimestampTool.output') }}</span>
          <span class="ts-panel-title">{{ $t('tool.TimestampTool.dateToTs') }}</span>
          <span class="ts-panel-sub">{{ $t('tool.TimestampTool.dateInputHint') }}</span>
        </div>
        <div class="ts-panel-input-row">
          <input class="ts-input" v-model="dtInput" @input="dtToTs" :placeholder="$t('tool.TimestampTool.phDate')"
            @keyup.enter="dtToTs"/>
          <button class="ts-btn-sm ts-btn-now" @click="dtToTs(null,true)">{{ $t('tool.TimestampTool.nowBtn') }}</button>
        </div>
        <div class="ts-panel-err" v-if="dtInput && dtErr">{{ dtErr }}</div>
        <div class="ts-panel-result" v-if="dtResult">
          <div class="ts-result-row" v-for="r in dtResult" :key="r.label">
            <span class="ts-result-label">{{ r.label }}</span>
            <code class="ts-result-val">{{ r.value }}</code>
            <button v-if="r.copy" class="ts-btn-icon ts-btn-sml" @click="copy(r.value)" title="复制">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            </button>
          </div>
        </div>
        <div class="ts-panel-hint" v-if="!dtInput">
          <span>{{ $t('tool.TimestampTool.tryDate') }}</span>
          <button class="ts-hint-chip" @click="dtInput='2026-05-30 12:00';dtToTs()">2026-05-30 12:00</button>
        </div>
      </div>
    </div>

    <!-- Common timestamp references -->
    <div class="ts-ref">
      <div class="ts-ref-title">{{ $t('tool.TimestampTool.commonRefs') }}</div>
      <div class="ts-ref-grid">
        <div class="ts-ref-card" v-for="r in i18nRefs" :key="r.label" @click="tsInput=String(refTs(r));tsToDate()">
          <span class="ts-ref-label">{{ r.label }}</span>
          <code class="ts-ref-ts">{{ refTs(r) }}</code>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
function pad(n){return n<10?'0'+n:''+n}

export default {
  name:'TimestampTool',props:{config:{type:Object,default:()=>({})}},
  data:function(){
    return {
      nowSec:0,nowMs:'',nowDate:'',
      tsInput:'',tsResult:null,tsErr:'',
      dtInput:'',dtResult:null,dtErr:'',
      refs:[
        {key:'todayRef',ts:'today'},
        {key:'currentTimeRef',ts:'now'},
        {key:'year2038Ref',ts:2147483647},
      ]
    }
  },
  computed:{
    i18nRefs:function(){ var t=this; return this.refs.map(function(r){ return {label:t.$t('tool.TimestampTool.'+r.key),ts:r.ts} }) }
  },
  created:function(){this.tick();this.timer=setInterval(this.tick,1000)},
  beforeDestroy:function(){clearInterval(this.timer)},
  methods:{
    fmtLocal:function(d,withWeek){
      var s=d.getFullYear()+'-'+pad(d.getMonth()+1)+'-'+pad(d.getDate())+' '+pad(d.getHours())+':'+pad(d.getMinutes())+':'+pad(d.getSeconds())
      if(!withWeek)return s
      var days=this.$t('tool.TimestampTool.weekDays')
      return s+this.$t('tool.TimestampTool.weekPrefix')+days[d.getDay()]
    },
    tick:function(){
      var d=new Date();this.nowSec=Math.floor(d.getTime()/1000);this.nowMs=String(d.getTime());
      this.nowDate=this.fmtLocal(d,true)
    },
    tsToDate:function(){
      var v=this.tsInput.trim();this.tsResult=null;this.tsErr=''
      if(!v)return
      if(!/^\d+$/.test(v)){this.tsErr=this.$t('tool.TimestampTool.errDigits');return}
      var ts
      if(v.length===10)ts=parseInt(v)*1000
      else if(v.length===13)ts=parseInt(v)
      else{this.tsErr=this.$t('tool.TimestampTool.errLength');return}
      var d=new Date(ts)
      if(isNaN(d.getTime())){this.tsErr=this.$t('tool.TimestampTool.errInvalidTs');return}
      this.tsResult=[
        {label:this.$t('tool.TimestampTool.resultLocalTime'),value:this.fmtLocal(d,true),copy:true},
        {label:this.$t('tool.TimestampTool.resultUtcTime'),value:d.toISOString().replace('T',' ').slice(0,19)+' UTC',copy:true},
        {label:this.$t('tool.TimestampTool.resultIso8601'),value:d.toISOString(),copy:true},
      ]
    },
    dtToTs:function(e,isNow){
      if(isNow){var nd=new Date();this.dtInput=nd.getFullYear()+'-'+pad(nd.getMonth()+1)+'-'+pad(nd.getDate())+' '+pad(nd.getHours())+':'+pad(nd.getMinutes())+':'+pad(nd.getSeconds())}
      var v=this.dtInput.trim();this.dtResult=null;this.dtErr=''
      if(!v)return
      var d=new Date(v.replace(' ','T'))
      if(isNaN(d.getTime())){this.dtErr=this.$t('tool.TimestampTool.errUnrecognized');return}
      this.dtResult=[
        {label:this.$t('tool.TimestampTool.resultUnixSec'),value:String(Math.floor(d.getTime()/1000)),copy:true},
        {label:this.$t('tool.TimestampTool.resultUnixMs'),value:String(d.getTime()),copy:true},
        {label:this.$t('tool.TimestampTool.resultDateLocal'),value:this.fmtLocal(d,true),copy:false},
      ]
    },
    refTs:function(r){if(r.ts==='now')return this.nowSec;if(r.ts==='today'){var d=new Date();d.setHours(0,0,0,0);return Math.floor(d.getTime()/1000)}return r.ts},
    copy:function(t){navigator.clipboard.writeText(t);this.$message.success(this.$t("common.status.copied"))},
    doAction:function(a){if(a==='clear'){this.tsInput='';this.tsResult=null;this.tsErr='';this.dtInput='';this.dtResult=null;this.dtErr=''}}
  }
}
</script>

<style scoped>
.ts-app{max-width:100%;display:flex;flex-direction:column;gap:18px}

/* === 顶部实时时间戳 === */
.ts-live-bar{display:flex;align-items:center;gap:16px;background:linear-gradient(135deg,#f0f4ff,#fafbff);border:1.5px solid #d6e0f5;border-radius:14px;padding:16px 22px;overflow-x:auto}
.ts-live-item{display:flex;align-items:center;gap:8px;white-space:nowrap}
.ts-live-label{font-size:11px;color:#7b8bb0;font-weight:600;text-transform:uppercase}
.ts-live-val{font-size:18px;font-weight:700;color:#1a1f36;font-family:'SF Mono',Menlo,Consolas,monospace;letter-spacing:.5px}
.ts-live-divider{width:1px;height:28px;background:rgba(0,0,0,.06);flex-shrink:0}
.ts-live-date{flex:1;min-width:0}
.ts-live-date-val{font-size:13px;color:#606288;font-family:'SF Mono',Menlo,Consolas,monospace}

/* === 双面板 === */
.ts-body{display:flex;gap:18px}
.ts-panel{flex:1;min-width:0;background:#fff;border:1px solid #e8ecf1;border-radius:14px;padding:20px 22px;transition:border-color .15s}
.ts-panel:hover{border-color:#d0d7e5}

.ts-panel-head{display:flex;align-items:center;gap:8px;margin-bottom:14px;flex-wrap:wrap}
.ts-panel-badge{padding:3px 10px;border-radius:12px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px}
.ts-panel-badge.in{background:#e8f0fe;color:#4e7cff}
.ts-panel-badge.out{background:#e8f8e8;color:#43a047}
.ts-panel-title{font-size:15px;font-weight:700;color:#1a1f36}
.ts-panel-sub{font-size:11px;color:#b0b8c4;margin-left:auto}

.ts-panel-input-row{display:flex;gap:8px}
.ts-input{flex:1;border:1.5px solid #e1e5e9;border-radius:10px;padding:11px 14px;font-size:15px;font-family:'SF Mono',Menlo,Consolas,monospace;color:#303133;outline:none;background:#fafbfc;transition:.15s}
.ts-input:focus{border-color:#4e7cff;background:#fff;box-shadow:0 0 0 3px rgba(78,124,255,.06)}
.ts-input::placeholder{color:#c0c8d4;font-size:13px}

/* 按钮 */
.ts-btn-icon{display:flex;align-items:center;justify-content:center;width:30px;height:30px;border:none;border-radius:7px;cursor:pointer;background:#e8ecf5;color:#8892a6;transition:.12s;flex-shrink:0}
.ts-btn-icon:hover{background:rgba(78,124,255,.15);color:#4e7cff}
.ts-btn-sml{width:24px;height:24px;border-radius:5px;background:#f0f4ff;color:#8a9bc0}
.ts-btn-sml:hover{background:#e0e8ff;color:#4e7cff}

.ts-btn-sm{padding:8px 16px;border:none;border-radius:9px;font-size:12px;font-weight:600;cursor:pointer;transition:.12s;white-space:nowrap}
.ts-btn-now{background:#f0f4ff;color:#4e7cff}
.ts-btn-now:hover{background:#e0e8ff}

/* 结果 */
.ts-panel-result{margin-top:14px;display:flex;flex-direction:column;gap:6px}
.ts-result-row{display:flex;align-items:center;gap:10px;padding:10px 14px;background:#f8f9fb;border-radius:9px}
.ts-result-label{font-size:11px;font-weight:600;color:#8892a6;width:110px;flex-shrink:0}
.ts-result-val{flex:1;font-size:13px;font-family:'SF Mono',Menlo,Consolas,monospace;color:#303133;word-break:break-all}

/* 错误 */
.ts-panel-err{margin-top:10px;font-size:12px;color:#e74c3c;padding:8px 12px;background:#fff5f5;border-radius:8px;border:1px solid #fde0e0}

/* 提示 */
.ts-panel-hint{margin-top:14px;display:flex;align-items:center;gap:6px;font-size:12px;color:#b0b8c4}
.ts-hint-chip{padding:3px 10px;border-radius:14px;font-size:11px;font-family:monospace;background:#f0f4ff;color:#4e7cff;border:1px solid #d0d9f0;cursor:pointer;transition:.12s}
.ts-hint-chip:hover{background:#e0e8ff}

/* === 参考区 === */
.ts-ref{background:#fafbfc;border:1px solid #ebeef5;border-radius:12px;padding:16px 20px}
.ts-ref-title{font-size:12px;font-weight:700;color:#909399;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px}
.ts-ref-grid{display:flex;flex-wrap:wrap;gap:8px}
.ts-ref-card{display:flex;flex-direction:column;gap:2px;padding:10px 16px;background:#fff;border:1px solid #e8ecf1;border-radius:10px;cursor:pointer;transition:.12s;min-width:120px}
.ts-ref-card:hover{border-color:#4e7cff;box-shadow:0 2px 8px rgba(78,124,255,.08)}
.ts-ref-label{font-size:12px;color:#606266;font-weight:500}
.ts-ref-ts{font-size:14px;font-family:monospace;color:#4e7cff;font-weight:600}

/* 响应式 */
@media(max-width:768px){
  .ts-body{flex-direction:column}
  .ts-live-bar{flex-wrap:wrap;gap:10px;padding:12px 16px}
  .ts-live-val{font-size:16px}
  .ts-live-divider{display:none}
  .ts-result-label{width:80px}
}
</style>
